
  /**
   * @license
   * author: epignosis front-end team
   * @epignosis_llc/gnosis.js v6.0.0
   * Released under the MIT license.
   */

import { objectWithoutProperties as _objectWithoutProperties, slicedToArray as _slicedToArray, extends as _extends } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import '../../../node_modules/core-js/modules/es.array.concat.js';
import '../../../node_modules/core-js/modules/es.array.map.js';
import '../../../node_modules/core-js/modules/web.set-interval.js';
import '../../../node_modules/core-js/modules/web.set-timeout.js';
import { useState, useRef, useEffect, useLayoutEffect, Fragment } from 'react';
import classNames from '../../../node_modules/classnames/index.js';
import useClickAway from '../../../node_modules/ahooks/es/useClickAway/index.js';
import Text from '../Text/Text.js';
import SearchInput from '../FormElements/Input/SearchInput.js';
import DropdownListItem from './components/DropdownListItem.js';
import DropdownListItemTitle from './components/DropdownListItemTitle.js';
import { DropdownList, DropdownContainer } from './styles.js';
import { getScrollableParent as _getScrollableParent, filterListByKeyword as _filterListByKeyword } from './helpers.js';
import { createElement as jsx } from '../../../node_modules/@emotion/react/dist/emotion-react.browser.esm.js';

var _excluded = ["list", "children", "placement", "onListItemSelect", "isSearchable", "textSize", "fullWidth", "prependContent", "hover", "fixPlacement", "emptyStateText", "placeholderText", "remainOpenOnSelect", "onToggleList", "disabled"];
var dropdownWrapperClasses = function dropdownWrapperClasses(placement) {
  return classNames("dropdown-wrapper", {
    "bottom-start": placement === "bottom-start",
    "bottom-end": placement === "bottom-end",
    "top-start": placement === "top-start",
    "top-end": placement === "top-end",
    "end-top": placement === "end-top"
  });
};
var dropdownButtonClasses = function dropdownButtonClasses(isListOpen) {
  return classNames("dropdown-button", {
    "is-active": isListOpen
  });
};
var RTLMapping = {
  "bottom-start": "bottom-end",
  "bottom-end": "bottom-start",
  "top-start": "top-end",
  "top-end": "top-start",
  "end-top": "end-top"
};
var Dropdown = function Dropdown(_ref) {
  var list = _ref.list,
    children = _ref.children,
    _ref$placement = _ref.placement,
    placement = _ref$placement === void 0 ? "bottom-start" : _ref$placement,
    onListItemSelect = _ref.onListItemSelect,
    _ref$isSearchable = _ref.isSearchable,
    isSearchable = _ref$isSearchable === void 0 ? false : _ref$isSearchable,
    _ref$textSize = _ref.textSize,
    textSize = _ref$textSize === void 0 ? "sm" : _ref$textSize,
    _ref$fullWidth = _ref.fullWidth,
    fullWidth = _ref$fullWidth === void 0 ? false : _ref$fullWidth,
    prependContent = _ref.prependContent,
    _ref$hover = _ref.hover,
    hover = _ref$hover === void 0 ? false : _ref$hover,
    _ref$fixPlacement = _ref.fixPlacement,
    fixPlacement = _ref$fixPlacement === void 0 ? false : _ref$fixPlacement,
    _ref$emptyStateText = _ref.emptyStateText,
    emptyStateText = _ref$emptyStateText === void 0 ? "No match found" : _ref$emptyStateText,
    _ref$placeholderText = _ref.placeholderText,
    placeholderText = _ref$placeholderText === void 0 ? "Search" : _ref$placeholderText,
    _ref$remainOpenOnSele = _ref.remainOpenOnSelect,
    remainOpenOnSelect = _ref$remainOpenOnSele === void 0 ? false : _ref$remainOpenOnSele,
    onToggleList = _ref.onToggleList,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === void 0 ? false : _ref$disabled,
    rest = _objectWithoutProperties(_ref, _excluded);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isListOpen = _useState2[0],
    setIsListOpen = _useState2[1];
  var _useState3 = useState(placement),
    _useState4 = _slicedToArray(_useState3, 2),
    currentPlacement = _useState4[0],
    setCurrentPlacement = _useState4[1];
  var _useState5 = useState(function () {
      return list;
    }),
    _useState6 = _slicedToArray(_useState5, 2),
    filteredList = _useState6[0],
    setFilteredList = _useState6[1];
  var shouldFocus = Boolean(isSearchable);
  var wrapperRef = useRef(null);
  var dropdownButtonRef = useRef(null);
  var dropdownWrapperRef = useRef(null);
  var dir = document.dir;
  var isRtl = dir === "rtl";
  var hoverTimeOut;
  useClickAway(function () {
    setIsListOpen(false);
  }, wrapperRef);
  useEffect(function () {
    setFilteredList(list);
  }, [list]);
  useEffect(function () {
    if (!disabled && onToggleList) {
      onToggleList(isListOpen);
    }
  }, [isListOpen]);
  useEffect(function () {
    if (disabled) {
      setIsListOpen(false);
      setFilteredList(list);
    }
  }, [disabled, list]);
  useLayoutEffect(function () {
    if (isListOpen) {
      fixPlacement && fixDropdownPlacement();
    }
  }, [isListOpen]);
  var fixDropdownPlacement = function fixDropdownPlacement() {
    if (!dropdownButtonRef.current || !dropdownWrapperRef.current || !wrapperRef.current) return;
    var dropdownRect = dropdownButtonRef.current.getBoundingClientRect();
    var scrollableParent = _getScrollableParent(wrapperRef.current.parentNode);
    var dropdownMenuHeight = dropdownWrapperRef.current.getBoundingClientRect().height;
    var dropdownMenuWidth = dropdownWrapperRef.current.getBoundingClientRect().width;
    var vertical = "bottom";
    var horizontal = "start";

    // Each case works with the same logic, but for different placements
    // We check if there is enough space on the right or left and top or bottom
    // and then we decide where to place the dropdown list
    // The dropdown list will be placed on the opposite side if there is not enough space
    // If there is a scrollable parent, we calculate the space based on the parent's dimensions
    // If there is no scrollable parent, we calculate the space based on the window's dimensions
    switch (placement) {
      case "bottom-start":
        {
          var dropdownButtonBottom = dropdownRect.bottom;
          if (!scrollableParent) {
            var spaceOnTheRight = window.innerWidth - dropdownRect.right;
            var spaceBelow = window.innerHeight - dropdownButtonBottom;
            horizontal = spaceOnTheRight < dropdownMenuWidth ? "end" : "start";
            vertical = spaceBelow < dropdownMenuHeight ? "top" : "bottom";
          }
          if (scrollableParent instanceof HTMLElement) {
            var _spaceOnTheRight = scrollableParent.clientWidth - dropdownRect.right;
            var _spaceBelow = scrollableParent.clientHeight - (dropdownButtonBottom - scrollableParent.getBoundingClientRect().top);
            horizontal = _spaceOnTheRight < dropdownMenuWidth ? "end" : "start";
            vertical = _spaceBelow < dropdownMenuHeight ? "top" : "bottom";
          }
          break;
        }
      case "bottom-end":
        {
          var _dropdownButtonBottom = dropdownRect.bottom;
          if (!scrollableParent) {
            var spaceOnTheLeft = dropdownRect.left;
            var _spaceBelow2 = window.innerHeight - _dropdownButtonBottom;
            horizontal = spaceOnTheLeft < dropdownMenuWidth ? "start" : "end";
            vertical = _spaceBelow2 < dropdownMenuHeight ? "top" : "bottom";
          }
          if (scrollableParent instanceof HTMLElement) {
            var _spaceOnTheLeft = dropdownRect.left;
            var _spaceBelow3 = scrollableParent.clientHeight - (_dropdownButtonBottom - scrollableParent.getBoundingClientRect().top);
            vertical = _spaceBelow3 < dropdownMenuHeight ? "top" : "bottom";
            horizontal = _spaceOnTheLeft < dropdownMenuWidth ? "start" : "end";
          }
          break;
        }
      case "top-start":
        {
          if (!scrollableParent) {
            var spaceAbove = dropdownRect.top;
            var _spaceOnTheRight2 = window.innerWidth - dropdownRect.right;
            horizontal = _spaceOnTheRight2 < dropdownMenuWidth ? "end" : "start";
            vertical = spaceAbove < dropdownMenuHeight ? "bottom" : "top";
          }
          if (scrollableParent instanceof HTMLElement) {
            var _spaceAbove = dropdownRect.top - scrollableParent.getBoundingClientRect().top;
            var _spaceOnTheRight3 = scrollableParent.clientWidth - dropdownRect.right;
            horizontal = _spaceOnTheRight3 < dropdownMenuWidth ? "end" : "start";
            vertical = _spaceAbove < dropdownMenuHeight ? "bottom" : "top";
          }
          break;
        }
      case "top-end":
        {
          if (!scrollableParent) {
            var _spaceAbove2 = dropdownRect.top;
            var _spaceOnTheLeft2 = dropdownRect.left;
            horizontal = _spaceOnTheLeft2 < dropdownMenuWidth ? "start" : "end";
            vertical = _spaceAbove2 < dropdownMenuHeight ? "bottom" : "top";
          }
          if (scrollableParent instanceof HTMLElement) {
            var _spaceAbove3 = dropdownRect.top - scrollableParent.getBoundingClientRect().top;
            var _spaceOnTheLeft3 = dropdownRect.left;
            horizontal = _spaceOnTheLeft3 < dropdownMenuWidth ? "start" : "end";
            vertical = _spaceAbove3 < dropdownMenuHeight ? "bottom" : "top";
          }
          break;
        }
    }
    var res = "".concat(vertical, "-").concat(horizontal);
    if (isRtl) {
      res = RTLMapping[res];
    }
    setCurrentPlacement(res);
  };

  // Container methods

  var handleOnKeyDown = function handleOnKeyDown(e) {
    if (e.key === "Enter") {
      setIsListOpen(function (prevState) {
        return !prevState;
      });
    }
  };
  var handleOnMouseOver = function handleOnMouseOver() {
    if (!hover) return;
    if (hoverTimeOut) clearTimeout(hoverTimeOut);
    setIsListOpen(true);
  };
  var handleOnMouseLeave = function handleOnMouseLeave() {
    if (!hover) return;

    // A small debounce to prevent dropdown from closing.
    hoverTimeOut = setTimeout(function () {
      setIsListOpen(false);
      // Force the list to be reset when the dropdown is closed
      setFilteredList(list);
    }, 100);
  };

  // List toggle element methods

  var toggleList = function toggleList(e) {
    e.stopPropagation();
    if (disabled || hover) return;

    // We want to reset the dropdown list every time it opens
    if (!isListOpen) {
      setFilteredList(list);
    }
    setIsListOpen(function (prevState) {
      return !prevState;
    });
  };

  // Search element methods

  var handleInputChanged = function handleInputChanged(keyword) {
    if (!keyword) setFilteredList(list);
    setFilteredList(_filterListByKeyword(list, keyword));
  };

  // List item methods

  var handleOnClickListItem = function handleOnClickListItem(item) {
    if (isListOpen && !disabled) {
      onListItemSelect && onListItemSelect(item);
      !remainOpenOnSelect && setIsListOpen(false);
    }
  };
  var handleOnKeyDownListItem = function handleOnKeyDownListItem(e, item) {
    if (e.key === "Enter" || e.key === " ") {
      handleOnClickListItem(item);
    }
  };
  var _renderItemsRecursively = function renderItemsRecursively(items) {
    var level = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
    return items.map(function (item, index) {
      var label = item.label,
        value = item.value,
        _item$items = item.items,
        childrenItems = _item$items === void 0 ? [] : _item$items;
      if (childrenItems.length > 0) {
        return jsx(Fragment, {
          key: "".concat(index, "-").concat(value)
        }, jsx(DropdownListItemTitle, {
          label: label,
          level: level,
          isSearchable: isSearchable,
          textSize: textSize
        }), _renderItemsRecursively(childrenItems, level + 1));
      }
      return jsx(DropdownListItem, {
        key: "item-".concat(index, "-").concat(value),
        index: index,
        item: item,
        isSearchable: isSearchable,
        level: level,
        textSize: textSize,
        onClick: handleOnClickListItem,
        onKeyDown: handleOnKeyDownListItem
      });
    });
  };
  return jsx("div", _extends({
    css: function css(theme) {
      return DropdownContainer(theme, {
        isSearchable: Boolean(isSearchable),
        fullWidth: fullWidth
      });
    },
    className: "dropdown",
    ref: wrapperRef,
    tabIndex: 0,
    onKeyDown: handleOnKeyDown,
    onMouseOver: handleOnMouseOver,
    onMouseLeave: handleOnMouseLeave
  }, rest), jsx("div", {
    className: dropdownButtonClasses(isListOpen),
    ref: dropdownButtonRef,
    onClick: toggleList
  }, children), isListOpen && jsx("div", {
    className: dropdownWrapperClasses(fixPlacement ? currentPlacement : placement),
    ref: dropdownWrapperRef
  }, prependContent, isSearchable && jsx(SearchInput, {
    id: "dropdown-search",
    placeholder: placeholderText,
    delayBeforeSearch: 300,
    autoFocus: shouldFocus,
    disabled: disabled,
    onInputChanged: handleInputChanged
  }), jsx("ul", {
    css: function css(theme) {
      return DropdownList(theme, {
        fullWidth: fullWidth,
        isSearchable: Boolean(isSearchable)
      });
    },
    role: "list",
    className: "dropdown-list"
  }, filteredList !== null && filteredList !== void 0 && filteredList.length ? _renderItemsRecursively(filteredList) : jsx("li", {
    className: "empty-state"
  }, jsx(Text, {
    fontSize: "xs",
    weight: "400"
  }, emptyStateText)))));
};

export { Dropdown as default };
//# sourceMappingURL=Dropdown.js.map
